// v49.24 — Recent navigation tracking.
// Per-user list of the last N routes the user visited. Persisted to
// localStorage keyed by user id so signed-in users have a separate
// history (also so we can drop the list on sign-out without nuking
// another user's state). Mirrors v48's lcRecentTrack pattern.

const STORAGE_PREFIX = "lc_recent_v1:";
const MAX_ENTRIES    = 12;

/* Routes we never track as 'recent' — they're not meaningful drill-ins. */
const SKIP_ROUTES = new Set([
  "/", "/signin", "/dashboard", "/inbox", "/notifications", "/audit", "/settings",
]);

export interface RecentEntry {
  path:    string;
  label:   string;
  at:      number;
}

function keyFor(userId: string | undefined | null): string {
  return STORAGE_PREFIX + (userId || "anon");
}

function readAll(userId: string | undefined | null): RecentEntry[] {
  try {
    const raw = localStorage.getItem(keyFor(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(e => e && typeof e.path === "string");
  } catch { return []; }
}

function writeAll(userId: string | undefined | null, list: RecentEntry[]) {
  try {
    localStorage.setItem(keyFor(userId), JSON.stringify(list));
    window.dispatchEvent(new CustomEvent("lc-recent-nav-changed"));
  } catch { /* ignore quota errors */ }
}

export function trackRecent(userId: string | undefined | null, path: string, label: string) {
  if (!path) return;
  /* Skip top-level destinations — only drill-in pages are interesting. */
  if (SKIP_ROUTES.has(path)) return;
  const existing = readAll(userId);
  const filtered = existing.filter(e => e.path !== path);
  const next: RecentEntry[] = [{ path, label, at: Date.now() }, ...filtered].slice(0, MAX_ENTRIES);
  writeAll(userId, next);
}

export function getRecent(userId: string | undefined | null): RecentEntry[] {
  return readAll(userId);
}

export function clearRecent(userId: string | undefined | null) {
  try { localStorage.removeItem(keyFor(userId)); } catch { /* ignore */ }
}
